'use client'

import { Mail, Send } from 'lucide-react'
import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react'
import {
  NCP_CONTACT_ENDPOINT,
  NCP_LOCAL_FORM_BYPASS,
  NCP_RECAPTCHA_ENABLED,
  NCP_RECAPTCHA_SITE_KEY,
} from '@/lib/ncp-env'

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}

export interface ContactFormLabels {
  formTitle?: string
  formDescription?: string
  nameLabel?: string
  namePlaceholder?: string
  emailLabel?: string
  emailPlaceholder?: string
  phoneLabel?: string
  phonePlaceholder?: string
  subjectLabel?: string
  subjectPlaceholder?: string
  messageLabel?: string
  messagePlaceholder?: string
  messageHelp?: string
  sendCta?: string
  sendingCta?: string
  sentCta?: string
  successMessage?: string
  submitErrorFallback?: string
  endpointNotConfigured?: string
  responseTimeNote?: string
}

interface ContactFormProps {
  labels?: ContactFormLabels
  sectionId?: string
}

export default function ContactForm({ labels = {}, sectionId = 'contact' }: ContactFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(
    !NCP_RECAPTCHA_ENABLED || NCP_LOCAL_FORM_BYPASS
  )

  // Pre-fill Subject when arriving from an "Apply" link, e.g. /en/contact?subject=Course%20application%3A%20IT
  useEffect(() => {
    const subject = new URLSearchParams(window.location.search).get('subject')
    if (subject) {
      setFormData((prev) => (prev.subject ? prev : { ...prev, subject: subject.slice(0, 200) }))
    }
  }, [])

  useEffect(() => {
    const contactSection = document.getElementById(sectionId)
    if (!contactSection) return

    let scriptLoadObserver: IntersectionObserver | null = null

    const loadRecaptchaScript = () => {
      if (typeof window === 'undefined') return
      if (!NCP_RECAPTCHA_ENABLED) {
        setRecaptchaLoaded(true)
        return
      }

      if (window.grecaptcha) {
        window.grecaptcha.ready(() => setRecaptchaLoaded(true))
        return
      }

      const script = document.createElement('script')
      script.src = `https://www.google.com/recaptcha/api.js?render=${NCP_RECAPTCHA_SITE_KEY}`
      script.async = true
      script.defer = true
      script.onload = () => {
        window.grecaptcha.ready(() => setRecaptchaLoaded(true))
      }
      document.head.appendChild(script)
    }

    scriptLoadObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadRecaptchaScript()
            scriptLoadObserver?.disconnect()
          }
        })
      },
      { rootMargin: '200px' }
    )

    scriptLoadObserver.observe(contactSection)

    return () => {
      scriptLoadObserver?.disconnect()
    }
  }, [sectionId])

  useEffect(() => {
    if (!recaptchaLoaded || !NCP_RECAPTCHA_ENABLED) return

    const contactSection = document.getElementById(sectionId)
    if (!contactSection) return

    let badgeObserver: IntersectionObserver | null = null
    let scrollHandler: (() => void) | null = null
    let retryTimeout: ReturnType<typeof setTimeout> | null = null

    const setupBadgeVisibility = () => {
      const badge = document.querySelector('.grecaptcha-badge') as HTMLElement | null
      if (!badge) {
        retryTimeout = setTimeout(setupBadgeVisibility, 500)
        return
      }

      const updateBadgeVisibility = (isVisible: boolean) => {
        if (isVisible) {
          badge.classList.add('contact-section-visible')
        } else {
          badge.classList.remove('contact-section-visible')
        }
      }

      badgeObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => updateBadgeVisibility(entry.isIntersecting))
        },
        { threshold: 0.1 }
      )

      badgeObserver.observe(contactSection)

      scrollHandler = () => {
        const rect = contactSection.getBoundingClientRect()
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0
        updateBadgeVisibility(isVisible)
      }

      window.addEventListener('scroll', scrollHandler, { passive: true })
      scrollHandler()
    }

    setupBadgeVisibility()

    return () => {
      badgeObserver?.disconnect()
      if (scrollHandler) window.removeEventListener('scroll', scrollHandler)
      if (retryTimeout) clearTimeout(retryTimeout)
    }
  }, [recaptchaLoaded, sectionId])

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)

    try {
      if (NCP_LOCAL_FORM_BYPASS) {
        await new Promise((resolve) => setTimeout(resolve, 400))
        setSubmitted(true)
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' })
        setTimeout(() => setSubmitted(false), 5000)
        return
      }

      if (!NCP_CONTACT_ENDPOINT) {
        throw new Error('Contact endpoint is not configured')
      }

      let recaptchaToken = ''
      if (NCP_RECAPTCHA_ENABLED) {
        if (!recaptchaLoaded || !window.grecaptcha) {
          throw new Error('reCAPTCHA is still loading. Please wait a moment and try again.')
        }
        try {
          await new Promise<void>((resolve) => window.grecaptcha.ready(resolve))
          recaptchaToken = await window.grecaptcha.execute(NCP_RECAPTCHA_SITE_KEY, {
            action: 'submit_ncp_contact_form',
          })
        } catch (recaptchaError) {
          console.error('reCAPTCHA error:', recaptchaError)
          throw new Error('reCAPTCHA verification failed. Please refresh the page and try again.')
        }
        if (!recaptchaToken) {
          throw new Error('reCAPTCHA verification failed. Please refresh the page and try again.')
        }
      }

      const response = await fetch(NCP_CONTACT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          fullName: formData.name,
          email: formData.email,
          phone: formData.phone,
          level: formData.subject,
          format: 'contact',
          message: formData.message,
          // Picks the language of the automatic thank-you email
          locale: window.location.pathname.startsWith('/ja') ? 'ja' : 'en',
          recaptchaToken,
        }),
      })

      const json = await response.json().catch(() => null)
      if (!response.ok) {
        throw new Error(json?.error || 'Failed to submit contact form')
      }
      if (json && json.ok === false) {
        throw new Error(json.error || 'Failed to submit contact form')
      }

      setSubmitted(true)
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' })
      setTimeout(() => setSubmitted(false), 5000)
    } catch (submitError) {
      console.error('NCP contact form error:', submitError)
      const fallback =
        labels.submitErrorFallback || 'There was an error sending your message. Please try again.'
      const message = submitError instanceof Error ? submitError.message : ''
      setError(
        message === 'Contact endpoint is not configured'
          ? labels.endpointNotConfigured ||
              'Contact endpoint is not configured. Please contact the administrator.'
          : message === 'Failed to fetch'
            ? 'Could not reach the contact service. Please check your connection and try again.'
            : message || fallback
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const submitDisabled =
    isSubmitting ||
    submitted ||
    (NCP_RECAPTCHA_ENABLED && !NCP_LOCAL_FORM_BYPASS && !recaptchaLoaded)

  return (
    <div className="ncp-form-card w-full h-full flex flex-col">
      <div className="mb-6 space-y-2">
        <h2 className="type-card-title">{labels.formTitle || 'Send us a message'}</h2>
        {labels.formDescription && (
          <p className="type-body text-muted">{labels.formDescription}</p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 flex-1 flex flex-col">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="ncp-field-label">
              {labels.nameLabel || 'Name'} <span className="ncp-field-required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              className="ncp-field-input focus-ring"
              placeholder={labels.namePlaceholder || 'Your full name'}
              autoComplete="name"
            />
          </div>

          <div>
            <label htmlFor="email" className="ncp-field-label">
              {labels.emailLabel || 'Email'} <span className="ncp-field-required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="ncp-field-input focus-ring"
              placeholder={labels.emailPlaceholder || 'your.email@example.com'}
              autoComplete="email"
            />
          </div>
        </div>

        <div>
          <label htmlFor="phone" className="ncp-field-label">
            {labels.phoneLabel || 'Phone'} <span className="ncp-field-required">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            required
            value={formData.phone}
            onChange={handleChange}
            className="ncp-field-input focus-ring"
            placeholder={labels.phonePlaceholder || '+92 300 1234567'}
            autoComplete="tel"
          />
        </div>

        <div>
          <label htmlFor="subject" className="ncp-field-label">
            {labels.subjectLabel || 'Subject'} <span className="ncp-field-required">*</span>
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            required
            value={formData.subject}
            onChange={handleChange}
            className="ncp-field-input focus-ring"
            placeholder={labels.subjectPlaceholder || 'What is this about?'}
          />
        </div>

        <div>
          <label htmlFor="message" className="ncp-field-label">
            {labels.messageLabel || 'Message'} <span className="ncp-field-required">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            required
            value={formData.message}
            onChange={handleChange}
            rows={5}
            className="ncp-field-input focus-ring resize-none min-h-[7.5rem]"
            placeholder={labels.messagePlaceholder || 'Tell us how we can help...'}
          />
          {labels.messageHelp && (
            <p className="mt-1.5 type-body text-muted text-xs">{labels.messageHelp}</p>
          )}
        </div>

        <div className="mt-auto space-y-3 pt-2">
        <button
          type="submit"
          disabled={submitDisabled}
          className={`btn-primary w-full gap-2 ${submitDisabled && !submitted ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {submitted
            ? labels.sentCta || 'Message sent'
            : isSubmitting
              ? labels.sendingCta || 'Sending...'
              : labels.sendCta || 'Send message'}
          {!submitted && !isSubmitting && <Send className="w-4 h-4" />}
        </button>

        {error && <p className="text-sm text-brand-red text-center">{error}</p>}
        {submitted && !error && (
          <p className="text-sm text-brand-green text-center font-medium">
            {labels.successMessage || 'Your message has been sent. Thank you!'}
          </p>
        )}
        {labels.responseTimeNote && (
          <p className="type-body text-muted text-xs text-center flex items-center justify-center gap-1.5">
            <Mail className="w-3.5 h-3.5 text-brand-green flex-shrink-0" />
            {labels.responseTimeNote}
          </p>
        )}
        </div>
      </form>
    </div>
  )
}
